import React, { useState } from 'react';
import { Store, MessageSquare, MapPin, Calendar, BarChart3, Send, Loader2, AlertCircle, TrendingUp, Star } from 'lucide-react';
import { moduleService } from '../../../services/modules';
import { MiniAppShell } from './MiniAppShell';

interface GBPAppProps {
    onExecutionStarted: (executionId: string) => void;
    onBack: () => void;
}

export const GBPApp = ({ onExecutionStarted, onBack }: GBPAppProps) => {
    const navItems = [
        { id: 'command', label: 'Command Center', icon: BarChart3 },
        { id: 'reviews', label: 'Review Hub', icon: MessageSquare },
        { id: 'rankings', label: 'Geo-Grid Tracker', icon: MapPin },
        { id: 'posts', label: 'Content Studio', icon: Calendar },
    ];

    return (
        <MiniAppShell
            title="GBP Optimization"
            icon={Store}
            navItems={navItems}
            onBack={onBack}
        >
            {(activeTab, setActiveTab) => {
                switch (activeTab) {
                    case 'command':
                        return <CommandCenterView />;
                    case 'reviews':
                        return <ReviewHubView onExecutionStarted={onExecutionStarted} />;
                    case 'rankings':
                        return <RankTrackerView />;
                    case 'posts':
                        return <ContentStudioView onExecutionStarted={onExecutionStarted} />;
                    default:
                        return <CommandCenterView />;
                }
            }}
        </MiniAppShell>
    );
};

const CommandCenterView = () => {
    const kpis = [
        { label: 'Search Views', value: '4,281', change: '+15%', icon: TrendingUp },
        { label: 'Map Views', value: '2,104', change: '+8%', icon: MapPin },
        { label: 'Website Clicks', value: '342', change: '+22%', icon: BarChart3 },
        { label: 'Phone Calls', value: '89', change: '+5%', icon: MessageSquare },
    ];

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Local Visibility Hub</h2>
                    <p className="text-xs text-slate-400 font-medium">Real-time Google Business Profile performance</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-600 text-[10px] font-black uppercase tracking-widest">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    Optimization Active
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {kpis.map((kpi, i) => (
                    <div key={i} className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="p-2 bg-rose-50 text-rose-600 rounded-xl">
                                <kpi.icon size={20} />
                            </div>
                            <span className="text-xs font-bold text-emerald-500">{kpi.change}</span>
                        </div>
                        <div>
                            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{kpi.label}</p>
                            <h3 className="text-3xl font-black text-slate-800">{kpi.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            <div className="p-8 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 text-slate-600 rounded-lg">
                        <BarChart3 size={20} />
                    </div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Visibility Trend</h3>
                </div>
                <div className="h-64 bg-slate-50 rounded-2xl border border-dashed border-slate-200 flex items-center justify-center text-slate-300 text-xs font-bold uppercase tracking-widest">
                    [ Interactive Visualization: Search vs Map Views ]
                </div>
            </div>
        </div>
    );
};

const ReviewHubView = ({ onExecutionStarted }: { onExecutionStarted: (id: string) => void }) => {
    const mockReviews = [
        { id: '1', user: 'Sarah J.', rating: 5, comment: 'Absolute game changer for my business!', date: '2 days ago' },
        { id: '2', user: 'Mike R.', rating: 4, comment: 'Great service, but delivery was slightly delayed.', date: '1 week ago' },
        { id: '3', user: 'Linda K.', rating: 5, comment: 'Professional and efficient!', date: '2 weeks ago' },
    ];

    const [activeReview, setActiveReview] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [loading, setLoading] = useState(false);

    const handlePublish = async () => {
        setLoading(true);
        try {
            const result = await moduleService.executeModule('gbp_opt', {
                action: 'respond_to_review',
                reviewId: activeReview,
                responseText: draft,
                requestedAt: new Date().toISOString(),
            });
            onExecutionStarted(result.executionId);
            setActiveReview(null);
            setDraft('');
            alert('Review response published!');
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Review Intelligence</h2>
                    <p className="text-xs text-slate-400 font-medium">AI-powered reputation management</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-3">
                    {mockReviews.map(review => (
                        <div
                            key={review.id}
                            onClick={() => setActiveReview(review.id)}
                            className={`p-4 bg-white border rounded-2xl cursor-pointer transition-all ${
                                activeReview === review.id ? 'border-rose-500 shadow-md' : 'border-slate-100 hover:border-slate-300'
                            }`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-bold text-slate-700">{review.user}</span>
                                <div className="flex text-amber-400">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} size={10} fill={i < review.rating ? 'currentColor' : 'none'} />
                                    ))}
                                </div>
                            </div>
                            <p className="text-xs text-slate-500 line-clamp-2 italic">"{review.comment}"</p>
                        </div>
                    ))}
                </div>

                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-8 shadow-sm space-y-6">
                    {activeReview ? (
                        <>
                            <div className="space-y-2">
                                <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Draft AI Response</h3>
                                <p className="text-xs text-slate-400 font-medium">Edit the AI suggestion before publishing</p>
                            </div>
                            <div className="relative">
                                <textarea
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    className="w-full p-4 rounded-2xl border border-slate-200 text-sm focus:ring-2 ring-rose-500 outline-none transition-all bg-slate-50"
                                    rows={5}
                                    placeholder="AI is drafting a response..."
                                />
                                <button
                                    onClick={() => setDraft(`Thank you so much for the 5-star review, ${mockReviews.find(r => r.id === activeReview)?.user}! We appreciate your support.`)}
                                    className="absolute bottom-3 right-3 p-2 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-slate-200 transition-all"
                                >
                                    Regenerate AI
                                </button>
                            </div>
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => setActiveReview(null)}
                                    className="px-4 py-2 text-slate-400 hover:text-slate-800 transition-all text-xs font-bold uppercase tracking-widest"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePublish}
                                    disabled={loading || !draft}
                                    className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? <Loader2 size={14} className="animate-spin" /> : 'Publish Response'}
                                    {!loading && <Send size={14} />}
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center p-8 text-slate-300">
                            <MessageSquare size={48} className="mb-4" />
                            <p className="text-xs font-bold uppercase tracking-widest">Select a review to respond</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const RankTrackerView = () => {
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Geo-Grid Rank Tracker</h2>
                    <p className="text-xs text-slate-400 font-medium">Visualizing your local dominance across the city</p>
                </div>
                <div className="flex gap-2">
                    <span className="px-3 py-1 bg-slate-100 text-slate-500 text-[10px] font-black rounded-full uppercase tracking-widest">
                        City Center
                    </span>
                </div>
            </div>

            <div className="aspect-video bg-slate-200 rounded-3xl border border-slate-300 overflow-hidden relative">
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center space-y-2">
                        <MapPin size={48} className="mx-auto text-slate-300" />
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Interactive Heatmap Loading...</p>
                    </div>
                </div>
                {/* Grid Simulation */}
                <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 pointer-events-none opacity-30">
                    {[...Array(100)].map((_, i) => (
                        <div key={i} className={`border border-slate-300 ${Math.random() > 0.7 ? 'bg-emerald-400' : 'bg-transparent'}`} />
                    ))}
                </div>
            </div>
        </div>
    );
};

const ContentStudioView = ({ onExecutionStarted }: { onExecutionStarted: (id: string) => void }) => {
    const [postContent, setPostContent] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSchedule = async () => {
        setLoading(true);
        try {
            const result = await moduleService.executeModule('gbp_opt', {
                action: 'schedule_post',
                content: postContent,
                requestedAt: new Date().toISOString(),
            });
            onExecutionStarted(result.executionId);
            setPostContent('');
            alert('Post scheduled successfully!');
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Content Studio</h2>
                    <p className="text-xs text-slate-400 font-medium">Automate your GBP posts and updates</p>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm space-y-6">
                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Post Content</label>
                    <textarea
                        value={postContent}
                        onChange={(e) => setPostContent(e.target.value)}
                        className="w-full p-4 rounded-2xl border border-slate-200 text-sm focus:ring-2 ring-rose-500 outline-none transition-all bg-slate-50"
                        rows={4}
                        placeholder="What's happening at your business? (e.g. New Summer Sale starting now!)"
                    />
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex gap-4">
                        <div className="flex items-center gap-2 p-2 bg-slate-100 rounded-lg text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                            <Calendar size={12} /> Schedule: Tomorrow 9AM
                        </div>
                    </div>
                    <button
                        onClick={handleSchedule}
                        disabled={loading || !postContent}
                        className="px-8 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : 'Schedule Post'}
                        {!loading && <Send size={14} />}
                    </button>
                </div>
            </div>
        </div>
    );
};
